import type { AssertTrue, IsNever } from "conditional-type-checks";
import { difference } from "lodash";
import type { WebhookTopic } from "shopify-api-node";
import { getShopifyModelApiIdentifierByModelKey } from "./apiIdentifiers";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { shopifyModelMetadataForVersion } from "./ShopifyModelMetadata";
import { WebhookApiUpgradeItem } from "./utils";

// This list combines topics listed by shopify-api-node as well as any others we subscribe to
export type ShopifyWebhookTopic =
  | WebhookTopic
  | (
      | "app_purchases_one_time/update"
      | "app_subscriptions/update"
      | "customer_payment_methods/create"
      | "customer_payment_methods/revoke"
      | "customer_payment_methods/update"
      | "customers/data_request"
      | "customers/redact"
      | "disputes/create"
      | "disputes/update"
      | "domains/create"
      | "domains/update"
      | "domains/destroy"
      | "locations/activate"
      | "locations/deactivate"
      | "scheduled_product_listings/add"
      | "scheduled_product_listings/remove"
      | "scheduled_product_listings/update"
      | "shop/redact"
      | "fulfillment_orders/order_routing_complete"
      | "fulfillment_orders/fulfillment_request_submitted"
      | "fulfillment_orders/fulfillment_request_accepted"
      | "fulfillment_orders/fulfillment_request_rejected"
      | "fulfillment_orders/placed_on_hold"
      | "fulfillment_orders/cancellation_request_accepted"
      | "fulfillment_orders/cancellation_request_rejected"
      | "fulfillment_orders/cancelled"
      | "fulfillment_orders/fulfillment_service_failed_to_complete"
      | "fulfillment_orders/moved"
      | "fulfillment_orders/split"
      | "fulfillment_orders/merged"
      | "fulfillment_orders/cancellation_request_submitted"
      | "fulfillment_orders/hold_released"
      | "fulfillment_orders/line_items_prepared_for_local_delivery"
      | "fulfillment_orders/line_items_prepared_for_pickup"
      | "fulfillment_orders/rescheduled"
      | "fulfillment_orders/scheduled_fulfillment_order_ready"
      | "markets/create"
      | "markets/update"
      | "markets/delete"
      | "companies/create"
      | "companies/update"
      | "companies/delete"
      | "company_contacts/create"
      | "company_contacts/update"
      | "company_contacts/delete"
      | "company_locations/create"
      | "company_locations/update"
      | "company_locations/delete"
      | "discounts/create"
      | "discounts/update"
      | "discounts/delete"
      | "orders/risk_assessment_changed"
      | "channels/delete"
      | "collection_publications/create"
      | "collection_publications/delete"
      | "collection_publications/update"
      | "customer.tags_added"
      | "customer.tags_removed"
      | "customer.joined_segment"
      | "customer.left_segment"
      | "attributed_sessions/first"
      | "attributed_sessions/last"
      | "product_publications/create"
      | "product_publications/delete"
      | "product_publications/update"
      | "segments/create"
      | "segments/delete"
      | "segments/update"
      | "shipping_addresses/create"
      | "shipping_addresses/update"
      | "tax_partners/update"
      | "tax_services/create"
      | "tax_services/update"
      | "variants/in_stock"
      | "variants/out_of_stock"
      | "app_subscriptions/approaching_capped_amount"
      | "returns/cancel"
      | "returns/close"
      | "returns/reopen"
      | "returns/request"
      | "returns/approve"
      | "returns/update"
      | "returns/decline"
      | "reverse_deliveries/attach_deliverable"
      | "reverse_fulfillment_orders/dispose"
      | "payment_terms/create"
      | "payment_terms/delete"
      | "payment_terms/update"
      | "product_feeds/create"
      | "product_feeds/update"
      | "orders/shopify_protect_eligibility_changed"
      | "publications/delete"
      | "audit_events/admin_api_activity"
      | "subscription_contracts/activate"
      | "subscription_contracts/pause"
      | "subscription_contracts/cancel"
      | "subscription_contracts/fail"
      | "subscription_contracts/expire"
      | "subscription_billing_cycles/skip"
      | "subscription_billing_cycles/unskip"
      | "metaobjects/create"
      | "metaobjects/update"
      | "metaobjects/delete"
      | "discounts/redeemcode_added"
      | "discounts/redeemcode_removed"
      | "metafield_definitions/create"
      | "metafield_definitions/delete"
      | "metafield_definitions/update"
      | "app/scopes_update"
      | "customers/purchasing_summary"
      | "customer.joined_segment"
      | "customer.left_segment"
      | "markets_backup_region/update"
      | "customer_account_settings/update"
      | "product_feeds/full_sync_finish"
      | "delivery_promise_settings/update"
      | "fulfillment_holds/added"
      | "fulfillment_holds/released"
      | "checkout_and_accounts_configurations/update"
      | "finance_app_staff_member/delete"
      | "finance_app_staff_member/grant"
      | "finance_app_staff_member/revoke"
      | "finance_app_staff_member/update"
      | "finance_kyc_information/update"
      | "inventory_shipments/add_items"
      | "inventory_shipments/create"
      | "inventory_shipments/delete"
      | "inventory_shipments/mark_in_transit"
      | "inventory_shipments/receive_items"
      | "inventory_shipments/remove_items"
      | "inventory_shipments/update_item_quantities"
      | "inventory_shipments/update_tracking"
      | "inventory_transfers/add_items"
      | "inventory_transfers/cancel"
      | "inventory_transfers/complete"
      | "inventory_transfers/ready_to_ship"
      | "inventory_transfers/remove_items"
      | "inventory_transfers/update_item_quantities"
      | "returns/process"
    );

export const SHOPIFY_WEBHOOKS = {
  "2021-10": [
    "app/uninstalled",
    "app_purchases_one_time/update",
    "app_subscriptions/update",
    "bulk_operations/finish",
    "carts/create",
    "carts/update",
    "checkouts/create",
    "checkouts/delete",
    "checkouts/update",
    "collection_listings/add",
    "collection_listings/remove",
    "collection_listings/update",
    "collections/create",
    "collections/delete",
    "collections/update",
    "customer_payment_methods/create",
    "customer_payment_methods/revoke",
    "customer_payment_methods/update",
    "customers/create",
    "customers/data_request",
    "customers/delete",
    "customers/disable",
    "customers/enable",
    "customers/redact",
    "customers/update",
    "customers_marketing_consent/update",
    "disputes/create",
    "disputes/update",
    "domains/create",
    "domains/destroy",
    "domains/update",
    "draft_orders/create",
    "draft_orders/delete",
    "draft_orders/update",
    "fulfillment_events/create",
    "fulfillment_events/delete",
    "fulfillments/create",
    "fulfillments/update",
    "inventory_items/create",
    "inventory_items/delete",
    "inventory_items/update",
    "inventory_levels/connect",
    "inventory_levels/disconnect",
    "inventory_levels/update",
    "locations/create",
    "locations/delete",
    "locations/update",
    "order_transactions/create",
    "orders/cancelled",
    "orders/create",
    "orders/delete",
    "orders/edited",
    "orders/fulfilled",
    "orders/paid",
    "orders/partially_fulfilled",
    "orders/updated",
    "product_listings/add",
    "product_listings/remove",
    "product_listings/update",
    "products/create",
    "products/delete",
    "products/update",
    "refunds/create",
    "selling_plan_groups/create",
    "selling_plan_groups/delete",
    "selling_plan_groups/update",
    "shop/redact",
    "shop/update",
    "subscription_billing_attempts/challenged",
    "subscription_billing_attempts/failure",
    "subscription_billing_attempts/success",
    "subscription_contracts/create",
    "subscription_contracts/update",
    "tender_transactions/create",
    "themes/create",
    "themes/delete",
    "themes/publish",
    "themes/update",
  ],
  "2022-01": ["scheduled_product_listings/add", "scheduled_product_listings/remove", "scheduled_product_listings/update"],
  "2022-04": ["markets/create", "markets/update", "markets/delete"],
  "2023-01": [
    "locations/activate",
    "locations/deactivate",
    "fulfillment_orders/order_routing_complete",
    "fulfillment_orders/fulfillment_request_submitted",
    "fulfillment_orders/fulfillment_request_accepted",
    "fulfillment_orders/fulfillment_request_rejected",
    "fulfillment_orders/placed_on_hold",
    "fulfillment_orders/cancellation_request_accepted",
    "fulfillment_orders/cancellation_request_rejected",
    "fulfillment_orders/cancelled",
    "fulfillment_orders/fulfillment_service_failed_to_complete",
  ],
  "2023-04": [
    "fulfillment_orders/moved",
    "companies/create",
    "companies/update",
    "companies/delete",
    "company_contacts/create",
    "company_contacts/update",
    "company_contacts/delete",
    "company_locations/create",
    "company_locations/update",
    "company_locations/delete",
  ],
  "2023-07": [
    "fulfillment_orders/cancellation_request_submitted",
    "fulfillment_orders/hold_released",
    "fulfillment_orders/line_items_prepared_for_local_delivery",
    "fulfillment_orders/line_items_prepared_for_pickup",
    "fulfillment_orders/rescheduled",
    "fulfillment_orders/scheduled_fulfillment_order_ready",
    "customer_groups/create",
    "customer_groups/delete",
    "customer_groups/update",
    "company_contact_roles/assign",
    "company_contact_roles/revoke",
    "customers/merge",
    "customers_email_marketing_consent/update",
    "locales/create",
    "locales/update",
    "payment_schedules/due",
    "product_feeds/full_sync",
    "product_feeds/incremental_sync",
    "profiles/create",
    "profiles/delete",
    "profiles/update",
    "subscription_billing_cycle_edits/create",
    "subscription_billing_cycle_edits/delete",
    "subscription_billing_cycle_edits/update",
    "customer.tags_added",
    "customer.tags_removed",
    "attributed_sessions/first",
    "attributed_sessions/last",
    "product_publications/create",
    "product_publications/delete",
    "product_publications/update",
    "segments/create",
    "segments/delete",
    "segments/update",
    "shipping_addresses/create",
    "shipping_addresses/update",
    "variants/in_stock",
    "variants/out_of_stock",
    "app_subscriptions/approaching_capped_amount",
    "returns/cancel",
    "returns/close",
    "returns/reopen",
    "returns/request",
    "returns/approve",
    "returns/decline",
    "reverse_deliveries/attach_deliverable",
    "reverse_fulfillment_orders/dispose",
    "product_feeds/create",
    "product_feeds/update",
    "audit_events/admin_api_activity",
    "tax_partners/update",
  ],
  "2023-10": ["orders/shopify_protect_eligibility_changed", "publications/delete"],
  "2024-01": [
    "channels/delete",
    "collection_publications/create",
    "collection_publications/delete",
    "collection_publications/update",
    "fulfillment_orders/split",
    "fulfillment_orders/merged",
    "discounts/create",
    "discounts/update",
    "discounts/delete",
    "subscription_contracts/activate",
    "subscription_contracts/pause",
    "subscription_contracts/cancel",
    "subscription_contracts/fail",
    "subscription_contracts/expire",
    "subscription_billing_cycles/skip",
    "subscription_billing_cycles/unskip",
    "metaobjects/create",
    "metaobjects/update",
    "metaobjects/delete",
    "discounts/redeemcode_added",
    "discounts/redeemcode_removed",
    "payment_terms/create",
    "payment_terms/delete",
    "payment_terms/update",
    "tax_services/create",
    "tax_services/update",
  ],
  "2024-04": ["orders/risk_assessment_changed", "returns/update"],
  "2024-07": ["customer_account_settings/update", "product_feeds/full_sync_finish"],
  "2024-10": ["metafield_definitions/create", "metafield_definitions/delete", "metafield_definitions/update", "app/scopes_update"],
  "2025-01": [
    "customers/purchasing_summary",
    "customer.joined_segment",
    "customer.left_segment",
    "delivery_promise_settings/update",
    "fulfillment_holds/added",
    "fulfillment_holds/released",
  ],
  "2025-04": [
    "markets_backup_region/update",
    "checkout_and_accounts_configurations/update",
    "finance_app_staff_member/delete",
    "finance_app_staff_member/grant",
    "finance_app_staff_member/revoke",
    "finance_app_staff_member/update",
    "finance_kyc_information/update",
  ],
  "2025-07": [
    "inventory_shipments/add_items",
    "inventory_shipments/create",
    "inventory_shipments/delete",
    "inventory_shipments/mark_in_transit",
    "inventory_shipments/receive_items",
    "inventory_shipments/remove_items",
    "inventory_shipments/update_item_quantities",
    "inventory_shipments/update_tracking",
    "inventory_transfers/add_items",
    "inventory_transfers/cancel",
    "inventory_transfers/complete",
    "inventory_transfers/ready_to_ship",
    "inventory_transfers/remove_items",
    "inventory_transfers/update_item_quantities",
    "returns/process",
  ],
  "2025-10": ["locales/destroy"],
}; // satisfies Partial<Record<ShopifyApiVersion, ShopifyWebhookTopic[]>>;   <-- TODO uncomment once fastify-renderer is happy with `satisfies` operator

export const GdprWebhookTopics = ["shop/redact", "customers/redact", "customers/data_request"] as const;

export const webhookScopesByTopic = new Map<ShopifyWebhookTopic, (apiVersion: ShopifyApiVersion) => string[]>([
  ["companies/create", (apiVersion) => (apiVersion >= "2024-01" ? ["read_customers", "read_companies"] : ["read_customers"])],
  ["companies/update", (apiVersion) => (apiVersion >= "2024-01" ? ["read_customers", "read_companies"] : ["read_customers"])],
  ["companies/delete", (apiVersion) => (apiVersion >= "2024-01" ? ["read_customers", "read_companies"] : ["read_customers"])],
  ["company_contacts/create", (apiVersion) => (apiVersion >= "2024-01" ? ["read_customers", "read_companies"] : ["read_customers"])],
  ["company_contacts/update", (apiVersion) => (apiVersion >= "2024-01" ? ["read_customers", "read_companies"] : ["read_customers"])],
  ["company_contacts/delete", (apiVersion) => (apiVersion >= "2024-01" ? ["read_customers", "read_companies"] : ["read_customers"])],
  ["company_locations/create", (apiVersion) => (apiVersion >= "2024-01" ? ["read_customers", "read_companies"] : ["read_customers"])],
  ["company_locations/update", (apiVersion) => (apiVersion >= "2024-01" ? ["read_customers", "read_companies"] : ["read_customers"])],
  ["company_locations/delete", (apiVersion) => (apiVersion >= "2024-01" ? ["read_customers", "read_companies"] : ["read_customers"])],
  ["app/uninstalled", () => []],
  ["app_purchases_one_time/update", () => []],
  ["app_subscriptions/update", () => []],
  ["bulk_operations/finish", () => []],
  ["carts/create", () => ["read_orders"]],
  ["carts/update", () => ["read_orders"]],
  ["checkouts/create", () => ["read_orders"]],
  ["checkouts/update", () => ["read_orders"]],
  ["checkouts/delete", () => ["read_orders"]],
  ["collections/create", () => ["read_products"]],
  ["collections/update", () => ["read_products"]],
  ["collections/delete", () => ["read_products"]],
  ["customers/create", () => ["read_customers"]],
  ["customers/update", () => ["read_customers"]],
  ["customers/delete", () => ["read_customers"]],
  ["customers/data_request", () => []],
  ["customers/redact", () => []],
  ["customer_payment_methods/create", () => ["read_customer_payment_methods"]],
  ["customer_payment_methods/update", () => ["read_customer_payment_methods"]],
  ["customer_payment_methods/revoke", () => ["read_customer_payment_methods"]],
  ["discounts/create", () => ["read_discounts"]],
  ["discounts/update", () => ["read_discounts"]],
  ["discounts/delete", () => ["read_discounts"]],
  ["disputes/create", () => ["read_shopify_payments_disputes"]],
  ["disputes/update", () => ["read_shopify_payments_disputes"]],
  ["domains/create", () => []],
  ["domains/update", () => []],
  ["domains/destroy", () => []],
  ["draft_orders/create", () => ["read_draft_orders"]],
  ["draft_orders/update", () => ["read_draft_orders"]],
  ["draft_orders/delete", () => ["read_draft_orders"]],
  ["fulfillment_events/create", () => ["read_fulfillments"]],
  ["fulfillment_events/delete", () => ["read_fulfillments"]],
  ["fulfillments/create", () => ["read_fulfillments", "read_marketplace_orders"]],
  ["fulfillments/update", () => ["read_fulfillments", "read_marketplace_orders"]],
  ["inventory_items/create", (apiVersion) => (apiVersion >= "2024-07" ? ["read_inventory", "read_products"] : ["read_inventory"])],
  ["inventory_items/update", (apiVersion) => (apiVersion >= "2024-07" ? ["read_inventory", "read_products"] : ["read_inventory"])],
  ["inventory_items/delete", (apiVersion) => (apiVersion >= "2024-07" ? ["read_inventory", "read_products"] : ["read_inventory"])],
  ["inventory_levels/connect", () => ["read_inventory"]],
  ["inventory_levels/update", () => ["read_inventory"]],
  ["inventory_levels/disconnect", () => ["read_inventory"]],
  ["locations/activate", () => ["read_locations"]],
  ["locations/create", () => ["read_locations"]],
  ["locations/deactivate", () => ["read_locations"]],
  ["locations/delete", () => ["read_locations"]],
  ["locations/update", () => ["read_locations"]],
  ["markets/create", () => ["read_markets"]],
  ["markets/delete", () => ["read_markets"]],
  ["markets/update", () => ["read_markets"]],
  ["order_transactions/create", () => ["read_orders", "read_marketplace_orders", "read_buyer_membership_orders"]],
  ["orders/create", () => ["read_orders", "read_marketplace_orders"]],
  ["orders/delete", () => ["read_orders"]],
  ["orders/updated", () => ["read_orders", "read_marketplace_orders", "read_buyer_membership_orders"]],
  ["orders/risk_assessment_changed", () => ["read_orders"]],
  ["refunds/create", () => ["read_orders", "read_marketplace_orders", "read_buyer_membership_orders"]],
  [
    "returns/approve",
    (apiVersion) => (apiVersion >= "2024-01" ? ["read_returns", "read_marketplace_returns", "read_buyer_membership_orders"] : []),
  ],
  [
    "returns/cancel",
    (apiVersion) =>
      apiVersion >= "2024-01"
        ? ["read_orders", "read_marketplace_orders", "read_returns", "read_marketplace_returns", "read_buyer_membership_orders"]
        : [],
  ],
  [
    "returns/close",
    (apiVersion) =>
      apiVersion >= "2024-01"
        ? ["read_orders", "read_marketplace_orders", "read_returns", "read_marketplace_returns", "read_buyer_membership_orders"]
        : [],
  ],
  [
    "returns/decline",
    (apiVersion) => (apiVersion >= "2024-01" ? ["read_returns", "read_marketplace_returns", "read_buyer_membership_orders"] : []),
  ],
  [
    "returns/process",
    (apiVersion) => (apiVersion >= "2024-01" ? ["read_returns", "read_marketplace_returns", "read_buyer_membership_orders"] : []),
  ],
  [
    "returns/reopen",
    (apiVersion) =>
      apiVersion >= "2024-01"
        ? ["read_orders", "read_marketplace_orders", "read_returns", "read_marketplace_returns", "read_buyer_membership_orders"]
        : [],
  ],
  [
    "returns/request",
    (apiVersion) => (apiVersion >= "2024-01" ? ["read_returns", "read_marketplace_returns", "read_buyer_membership_orders"] : []),
  ],
  [
    "returns/update",
    (apiVersion) => (apiVersion >= "2024-01" ? ["read_returns", "read_marketplace_returns", "read_buyer_membership_orders"] : []),
  ],
  ["payment_terms/create", () => ["read_payment_terms"]],
  ["payment_terms/update", () => ["read_payment_terms"]],
  ["payment_terms/delete", () => ["read_payment_terms"]],
  ["products/create", () => ["read_products"]],
  ["products/delete", () => ["read_products"]],
  ["products/update", () => ["read_products"]],
  [
    "fulfillment_orders/order_routing_complete",
    (apiVersion) => [
      "read_merchant_managed_fulfillment_orders",
      "read_assigned_fulfillment_orders",
      "read_third_party_fulfillment_orders",
      "read_marketplace_fulfillment_orders",
      "read_buyer_membership_orders",
    ],
  ],
  [
    "fulfillment_orders/fulfillment_request_submitted",
    (apiVersion) =>
      apiVersion >= "2024-01"
        ? [
            "read_merchant_managed_fulfillment_orders",
            "read_assigned_fulfillment_orders",
            "read_third_party_fulfillment_orders",
            "read_marketplace_fulfillment_orders",
          ]
        : [
            "read_merchant_managed_fulfillment_orders",
            "read_assigned_fulfillment_orders",
            "read_third_party_fulfillment_orders",
            "read_marketplace_fulfillment_orders",
            "read_buyer_membership_orders",
          ],
  ],
  [
    "fulfillment_orders/fulfillment_request_accepted",
    () => [
      "read_merchant_managed_fulfillment_orders",
      "read_assigned_fulfillment_orders",
      "read_third_party_fulfillment_orders",
      "read_marketplace_fulfillment_orders",
    ],
  ],
  [
    "fulfillment_orders/fulfillment_request_rejected",
    () => [
      "read_merchant_managed_fulfillment_orders",
      "read_assigned_fulfillment_orders",
      "read_third_party_fulfillment_orders",
      "read_marketplace_fulfillment_orders",
    ],
  ],
  [
    "fulfillment_orders/placed_on_hold",
    () => [
      "read_merchant_managed_fulfillment_orders",
      "read_assigned_fulfillment_orders",
      "read_third_party_fulfillment_orders",
      "read_marketplace_fulfillment_orders",
    ],
  ],
  [
    "fulfillment_orders/cancellation_request_submitted",
    () => [
      "read_merchant_managed_fulfillment_orders",
      "read_assigned_fulfillment_orders",
      "read_third_party_fulfillment_orders",
      "read_marketplace_fulfillment_orders",
    ],
  ],
  [
    "fulfillment_orders/cancellation_request_accepted",
    () => [
      "read_merchant_managed_fulfillment_orders",
      "read_assigned_fulfillment_orders",
      "read_third_party_fulfillment_orders",
      "read_marketplace_fulfillment_orders",
    ],
  ],
  [
    "fulfillment_orders/cancellation_request_rejected",
    () => [
      "read_merchant_managed_fulfillment_orders",
      "read_assigned_fulfillment_orders",
      "read_third_party_fulfillment_orders",
      "read_marketplace_fulfillment_orders",
    ],
  ],
  [
    "fulfillment_orders/cancelled",
    () => [
      "read_merchant_managed_fulfillment_orders",
      "read_assigned_fulfillment_orders",
      "read_third_party_fulfillment_orders",
      "read_marketplace_fulfillment_orders",
    ],
  ],
  [
    "fulfillment_orders/fulfillment_service_failed_to_complete",
    () => [
      "read_merchant_managed_fulfillment_orders",
      "read_assigned_fulfillment_orders",
      "read_third_party_fulfillment_orders",
      "read_marketplace_fulfillment_orders",
    ],
  ],
  [
    "fulfillment_orders/moved",
    () => [
      "read_merchant_managed_fulfillment_orders",
      "read_assigned_fulfillment_orders",
      "read_third_party_fulfillment_orders",
      "read_marketplace_fulfillment_orders",
    ],
  ],
  [
    "fulfillment_orders/split",
    (apiVersion) =>
      apiVersion >= "2024-01"
        ? ["read_merchant_managed_fulfillment_orders", "read_assigned_fulfillment_orders", "read_third_party_fulfillment_orders"]
        : [
            "read_merchant_managed_fulfillment_orders",
            "read_assigned_fulfillment_orders",
            "read_third_party_fulfillment_orders",
            "read_marketplace_fulfillment_orders",
          ],
  ],
  [
    "fulfillment_orders/merged",
    (apiVersion) =>
      apiVersion >= "2024-01"
        ? ["read_merchant_managed_fulfillment_orders", "read_assigned_fulfillment_orders", "read_third_party_fulfillment_orders"]
        : [
            "read_merchant_managed_fulfillment_orders",
            "read_assigned_fulfillment_orders",
            "read_third_party_fulfillment_orders",
            "read_marketplace_fulfillment_orders",
          ],
  ],
  [
    "fulfillment_orders/hold_released",
    () => [
      "read_merchant_managed_fulfillment_orders",
      "read_assigned_fulfillment_orders",
      "read_third_party_fulfillment_orders",
      "read_marketplace_fulfillment_orders",
    ],
  ],
  [
    "fulfillment_orders/line_items_prepared_for_local_delivery",
    () => [
      "read_merchant_managed_fulfillment_orders",
      "read_assigned_fulfillment_orders",
      "read_third_party_fulfillment_orders",
      "read_marketplace_fulfillment_orders",
    ],
  ],
  [
    "fulfillment_orders/line_items_prepared_for_pickup",
    () => [
      "read_merchant_managed_fulfillment_orders",
      "read_assigned_fulfillment_orders",
      "read_third_party_fulfillment_orders",
      "read_marketplace_fulfillment_orders",
    ],
  ],
  [
    "fulfillment_orders/rescheduled",
    () => [
      "read_merchant_managed_fulfillment_orders",
      "read_assigned_fulfillment_orders",
      "read_third_party_fulfillment_orders",
      "read_marketplace_fulfillment_orders",
    ],
  ],
  [
    "fulfillment_orders/scheduled_fulfillment_order_ready",
    () => [
      "read_merchant_managed_fulfillment_orders",
      "read_assigned_fulfillment_orders",
      "read_third_party_fulfillment_orders",
      "read_marketplace_fulfillment_orders",
    ],
  ],

  ["collection_listings/add", () => ["read_product_listings"]],
  ["collection_listings/remove", () => ["read_product_listings"]],
  ["collection_listings/update", () => ["read_product_listings"]],
  ["company_contact_roles/assign", (apiVersion) => (apiVersion >= "2024-01" ? ["read_customers", "read_companies"] : ["read_customers"])],
  ["company_contact_roles/revoke", (apiVersion) => (apiVersion >= "2024-01" ? ["read_customers", "read_companies"] : ["read_customers"])],
  ["customer_groups/create", () => ["read_customers"]],
  ["customer_groups/delete", () => ["read_customers"]],
  ["customer_groups/update", () => ["read_customers"]],
  ["customer_account_settings/update", () => []],
  ["customers/purchasing_summary", () => ["read_customers"]],
  ["customers_marketing_consent/update", () => ["read_customers"]],
  ["customers/disable", () => ["read_customers"]],
  ["customers/enable", () => ["read_customers"]],
  ["customers/merge", () => ["read_customer_merge"]],
  ["customers_email_marketing_consent/update", () => ["read_customers"]],
  ["locales/create", () => ["read_locales"]],
  ["locales/update", () => ["read_locales"]],
  ["locales/destroy", () => ["read_locales"]],
  ["markets_backup_region/update", () => ["read_markets"]],
  ["orders/cancelled", () => ["read_orders", "read_marketplace_orders", "read_buyer_membership_orders"]],
  ["orders/edited", () => ["read_orders", "read_marketplace_orders", "read_buyer_membership_orders"]],
  ["orders/fulfilled", () => ["read_orders", "read_marketplace_orders"]],
  ["orders/paid", () => ["read_orders", "read_marketplace_orders"]],
  ["orders/partially_fulfilled", () => ["read_orders", "read_marketplace_orders"]],
  ["payment_schedules/due", () => ["read_payment_terms"]],
  ["product_feeds/full_sync", () => ["read_product_listings"]],
  ["product_feeds/full_sync_finish", () => ["read_product_listings"]],
  ["product_feeds/incremental_sync", () => ["read_product_listings"]],
  ["product_listings/add", () => ["read_product_listings"]],
  ["product_listings/remove", () => ["read_product_listings"]],
  ["product_listings/update", () => ["read_product_listings"]],
  ["profiles/create", () => ["read_shipping", "read_assigned_shipping"]],
  ["profiles/delete", () => ["read_shipping", "read_assigned_shipping"]],
  ["profiles/update", () => ["read_shipping", "read_assigned_shipping"]],
  ["scheduled_product_listings/add", () => ["read_product_listings"]],
  ["scheduled_product_listings/remove", () => ["read_product_listings"]],
  ["scheduled_product_listings/update", () => ["read_product_listings"]],
  ["subscription_billing_cycle_edits/create", () => ["read_own_subscription_contracts"]],
  ["subscription_billing_cycle_edits/delete", () => ["read_own_subscription_contracts"]],
  ["subscription_billing_cycle_edits/update", () => ["read_own_subscription_contracts"]],
  ["themes/publish", () => ["read_themes"]],
  ["channels/delete", () => ["read_publications"]],
  ["collection_publications/create", () => ["read_publications"]],
  ["collection_publications/delete", () => ["read_publications"]],
  ["collection_publications/update", () => ["read_publications"]],
  ["customer.tags_added", () => ["read_customers"]],
  ["customer.tags_removed", () => ["read_customers"]],
  ["customer.joined_segment", () => ["read_customers"]],
  ["customer.left_segment", () => ["read_customers"]],
  ["attributed_sessions/first", () => ["read_marketing_events"]],
  ["attributed_sessions/last", () => ["read_marketing_events"]],
  ["product_publications/create", () => ["read_publications"]],
  ["product_publications/delete", () => ["read_publications"]],
  ["product_publications/update", () => ["read_publications"]],
  ["shipping_addresses/create", () => ["read_shipping"]],
  ["shipping_addresses/update", () => ["read_shipping"]],
  ["tax_services/create", () => ["read_taxes"]],
  ["tax_services/update", () => ["read_taxes"]],
  ["tax_partners/update", () => ["read_taxes"]],
  ["themes/create", () => ["read_themes"]],
  ["themes/update", () => ["read_themes"]],
  ["themes/delete", () => ["read_themes"]],
  ["variants/in_stock", () => ["read_products"]],
  ["variants/out_of_stock", () => ["read_products"]],
  ["app_subscriptions/approaching_capped_amount", () => []],
  ["reverse_deliveries/attach_deliverable", () => ["read_returns", "read_marketplace_returns"]],
  ["reverse_fulfillment_orders/dispose", () => ["read_returns", "read_marketplace_returns"]],
  ["product_feeds/create", () => ["read_product_listings"]],
  ["product_feeds/update", () => ["read_product_listings"]],
  ["orders/shopify_protect_eligibility_changed", () => ["read_orders"]],
  ["publications/delete", () => ["read_publications"]],
  ["audit_events/admin_api_activity", () => ["read_audit_events"]],
  ["subscription_contracts/activate", () => ["read_own_subscription_contracts"]],
  ["subscription_contracts/pause", () => ["read_own_subscription_contracts"]],
  ["subscription_contracts/cancel", () => ["read_own_subscription_contracts"]],
  ["subscription_contracts/fail", () => ["read_own_subscription_contracts"]],
  ["subscription_contracts/expire", () => ["read_own_subscription_contracts"]],
  ["subscription_billing_cycles/skip", () => ["read_own_subscription_contracts"]],
  ["subscription_billing_cycles/unskip", () => ["read_own_subscription_contracts"]],
  ["metaobjects/create", () => ["read_metaobjects"]],
  ["metaobjects/update", () => ["read_metaobjects"]],
  ["metaobjects/delete", () => ["read_metaobjects"]],
  ["discounts/redeemcode_added", () => ["read_discounts"]],
  ["discounts/redeemcode_removed", () => ["read_discounts"]],
  ["metafield_definitions/create", () => ["read_content"]],
  ["metafield_definitions/delete", () => ["read_content"]],
  ["metafield_definitions/update", () => ["read_content"]],
  ["app/scopes_update", () => []],
  [
    "fulfillment_holds/added",
    () => [
      "read_merchant_managed_fulfillment_orders",
      "read_assigned_fulfillment_orders",
      "read_third_party_fulfillment_orders",
      "read_marketplace_fulfillment_orders",
    ],
  ],
  [
    "fulfillment_holds/released",
    () => [
      "read_merchant_managed_fulfillment_orders",
      "read_assigned_fulfillment_orders",
      "read_third_party_fulfillment_orders",
      "read_marketplace_fulfillment_orders",
    ],
  ],
  ["delivery_promise_settings/update", () => ["read_shipping"]],
  ["checkout_and_accounts_configurations/update", () => []],
  ["finance_app_staff_member/delete", () => ["read_financial_kyc_information"]],
  ["finance_app_staff_member/grant", () => ["read_financial_kyc_information"]],
  ["finance_app_staff_member/revoke", () => ["read_financial_kyc_information"]],
  ["finance_app_staff_member/update", () => ["read_financial_kyc_information"]],
  ["finance_kyc_information/update", () => ["read_financial_kyc_information"]],
  ["inventory_shipments/add_items", () => ["read_inventory_shipments"]],
  ["inventory_shipments/create", () => ["read_inventory_shipments"]],
  ["inventory_shipments/delete", () => ["read_inventory_shipments"]],
  ["inventory_shipments/mark_in_transit", () => ["read_inventory_shipments"]],
  ["inventory_shipments/receive_items", () => ["read_inventory_shipments"]],
  ["inventory_shipments/remove_items", () => ["read_inventory_shipments"]],
  ["inventory_shipments/update_item_quantities", () => ["read_inventory_shipments"]],
  ["inventory_shipments/update_tracking", () => ["read_inventory_shipments"]],
  ["inventory_transfers/add_items", () => ["read_inventory_transfers"]],
  ["inventory_transfers/cancel", () => ["read_inventory_transfers"]],
  ["inventory_transfers/complete", () => ["read_inventory_transfers"]],
  ["inventory_transfers/ready_to_ship", () => ["read_inventory_transfers"]],
  ["inventory_transfers/remove_items", () => ["read_inventory_transfers"]],
  ["inventory_transfers/update_item_quantities", () => ["read_inventory_transfers"]],
  ["segments/create", () => ["read_customers"]],
  ["segments/delete", () => ["read_customers"]],
  ["segments/update", () => ["read_customers"]],
  ["selling_plan_groups/create", () => ["read_products"]],
  ["selling_plan_groups/delete", () => ["read_products"]],
  ["selling_plan_groups/update", () => ["read_products"]],
  ["shop/redact", () => []],
  ["shop/update", () => []],
  ["subscription_billing_attempts/challenged", () => ["read_own_subscription_contracts"]],
  ["subscription_billing_attempts/failure", () => ["read_own_subscription_contracts"]],
  ["subscription_billing_attempts/success", () => ["read_own_subscription_contracts"]],
  ["subscription_contracts/create", () => ["read_own_subscription_contracts"]],
  ["subscription_contracts/update", () => ["read_own_subscription_contracts"]],
  ["tender_transactions/create", () => ["read_orders"]],
]);

type _MissingWebhooks = Exclude<ShopifyWebhookTopic, (typeof SHOPIFY_WEBHOOKS)[keyof typeof SHOPIFY_WEBHOOKS][number]>;
type _AllWebhooksCovered = AssertTrue<IsNever<_MissingWebhooks>>;

export const groupedUpdatedWebhooks = (models: string[], currentApiVersion: ShopifyApiVersion, latestApiVersion: ShopifyApiVersion) => {
  const groupedWebhooks = models.reduce((result: Record<string, any>, model) => {
    const modelApiIdentifier = getShopifyModelApiIdentifierByModelKey(model, currentApiVersion);
    const currentMetadata = shopifyModelMetadataForVersion(model, currentApiVersion);
    const latestMetadata = shopifyModelMetadataForVersion(model, latestApiVersion);

    const currentWebhooks =
      currentMetadata?.inheritWebhooks !== true ? (Object.keys(currentMetadata?.webhooks ?? {}) as ShopifyWebhookTopic[]) : [];

    const latestWebhooks =
      latestMetadata?.inheritWebhooks !== true ? (Object.keys(latestMetadata?.webhooks ?? {}) as ShopifyWebhookTopic[]) : [];

    const deletedWebhookTopics = difference<ShopifyWebhookTopic>(currentWebhooks, latestWebhooks).map(
      (webhookTopic) =>
        ({
          topic: webhookTopic,
          contributingScopes: latestMetadata?.contributingScopes,
          modelKey: model,
          parentKey: latestMetadata?.parentKey,
          metaActionIdentifier: latestMetadata?.webhooks?.[webhookTopic]?.metaActionIdentifier,
        } as WebhookApiUpgradeItem)
    );

    const addedWebhookTopics = difference<ShopifyWebhookTopic>(latestWebhooks, currentWebhooks).map(
      (webhookTopic) =>
        ({
          topic: webhookTopic,
          contributingScopes: latestMetadata?.contributingScopes,
          modelKey: model,
          parentKey: latestMetadata?.parentKey,
          metaActionIdentifier: latestMetadata?.webhooks?.[webhookTopic]?.metaActionIdentifier,
        } as WebhookApiUpgradeItem)
    );

    if (addedWebhookTopics.length > 0 || deletedWebhookTopics.length > 0) {
      result[modelApiIdentifier] = { Added: [...addedWebhookTopics], Deleted: [...deletedWebhookTopics] };
    }

    return result;
  }, {});

  return groupedWebhooks;
};
